import React from "react";
import { Settings, Search, X, CalendarDays, LayoutGrid, Mail, Inbox } from "lucide-react";
import { GmailAccount, ViewType } from "../../types";
import Tooltip from "../ui/Tooltip";
import JudiMark from "../ui/JudiMark";
import { useCaptures } from "../../hooks/queries/useCaptures";

import { formatShortcut } from "../../lib/platform";

interface HeaderProps {
  onOpenSettings: () => void;
  searchTerm: string;
  searchHighlightKey?: number;
  onSearchClear: () => void;
  onSearchClick: () => void;
  viewType: ViewType;
  onViewTypeChange: (viewType: ViewType) => void;
  gmailAccounts?: GmailAccount[];
  gmailAccountCounts?: Record<string, number>;
  gmailConnected: boolean;
  emailCount?: number;
}

const Header: React.FC<HeaderProps> = ({
  onOpenSettings,
  searchTerm,
  searchHighlightKey = 0,
  onSearchClear,
  onSearchClick,
  viewType,
  onViewTypeChange,
  gmailAccounts,
  gmailAccountCounts,
  gmailConnected,
  emailCount,
}) => {
  const totalEmailCount =
    gmailAccounts && gmailAccounts.length > 0
      ? gmailAccounts.reduce(
          (sum, a) => sum + (gmailAccountCounts?.[a.gmail_email] ?? 0),
          0,
        )
      : emailCount ?? 0;
  const { data: captures = [] } = useCaptures();
  const captureCount = captures.length;
  return (
    <div
      className="sticky top-0 z-20 backdrop-blur-md border-b border-warm-border"
      style={{ backgroundColor: "var(--color-bg-card-translucent)" }}
    >
      <div className="px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-6 min-w-0 flex-shrink">
            <h1 className="flex-shrink-0 mr-1 sm:mr-0">
              <Tooltip content="Go to the board" placement="bottom" align="start">
                <button
                  type="button"
                  // On the board already, stay put: going to "/" again would
                  // drop the filters kept in the address.
                  onClick={() => {
                    if (viewType !== "board") onViewTypeChange("board");
                  }}
                  className="flex p-2.5 -m-2.5 rounded-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-blue"
                >
                  <JudiMark className="w-6 h-6 sm:w-7 sm:h-7" />
                  <span className="sr-only">JuDI</span>
                </button>
              </Tooltip>
            </h1>

            {/* Search Display */}
            {searchTerm ? (
              <div
                key={searchHighlightKey}
                role="button"
                tabIndex={0}
                onClick={onSearchClick}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSearchClick(); }}
                className={`relative overflow-hidden hidden sm:flex items-center gap-2 px-3.5 py-2 bg-warm-blue-light/20 text-warm-blue-dark rounded-soft text-base font-medium hover:bg-warm-blue-light/30 transition-colors duration-150 cursor-pointer min-w-0 ${searchHighlightKey > 0 ? "animate-search-chip-pop" : ""}`}
              >
                <Search size={16} className="flex-shrink-0" />
                <span className="max-w-[140px] sm:max-w-[220px] truncate">{searchTerm}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearchClear();
                  }}
                  aria-label="Clear search"
                  className="hover:opacity-70 transition-opacity duration-150 flex-shrink-0"
                >
                  <X size={16} />
                </button>
                {searchHighlightKey > 0 ? (
                  <div
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 w-full pointer-events-none animate-shine-sweep"
                    style={{
                      background:
                        "linear-gradient(105deg, transparent 15%, rgba(255,252,230,0.25) 30%, rgba(255,255,255,0.92) 50%, rgba(255,252,230,0.25) 70%, transparent 85%)",
                    }}
                  />
                ) : null}
              </div>
            ) : null}
            <Tooltip
              content="Search tasks (/)"
              placement="bottom"
            >
              <button
                onClick={onSearchClick}
                className={`p-2 sm:p-2.5 text-warm-text-secondary hover:text-warm-text-primary hover:bg-warm-bg-elevated rounded-soft transition-all duration-150 flex-shrink-0 ${searchTerm ? "sm:hidden" : ""}`}
                data-shortcut="/"
                aria-label="Search tasks"
              >
                {searchTerm ? (
                  <span className="relative inline-flex">
                    <Search size={18} />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-warm-blue rounded-full" />
                  </span>
                ) : (
                  <Search size={18} />
                )}
              </button>
            </Tooltip>
          </div>
          <div className="flex gap-1 sm:gap-2 items-center flex-shrink-0">
            {/* Emails button — only shown when a Gmail account is connected */}
            {gmailConnected && (
              <Tooltip content="Emails view (E)" placement="bottom">
                <button
                  onClick={() => onViewTypeChange("emails")}
                  aria-pressed={viewType === "emails"}
                  aria-label="Emails view"
                  data-shortcut="E"
                  data-shortcut-group="view-toggle"
                  className={`relative flex items-center justify-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors duration-150 ${
                    viewType === "emails"
                      ? "bg-warm-blue text-white border-warm-blue shadow-soft-sm"
                      : "bg-warm-bg-elevated text-warm-text-secondary border-warm-border hover:text-warm-text-primary"
                  }`}
                >
                  <Mail size={15} />
                  <span className="hidden sm:inline">Emails</span>
                  {totalEmailCount > 0 && (
                    <span
                      className={`min-w-[1.25rem] h-5 flex items-center justify-center rounded-full text-xs font-semibold tabular-nums px-1.5 ${
                        viewType === "emails"
                          ? "bg-white/20 text-white"
                          : "bg-warm-blue/10 text-warm-blue"
                      }`}
                    >
                      {totalEmailCount > 99 ? "99+" : totalEmailCount}
                    </span>
                  )}
                </button>
              </Tooltip>
            )}

            {/* Board / Calendar toggle */}
            <div
              className="relative inline-grid grid-cols-2 rounded-full p-1 bg-warm-bg-elevated border border-warm-border"
              role="group"
              aria-label="View"
              data-shortcut-group="view-toggle"
            >
              {/* Sliding pill */}
              <div
                aria-hidden="true"
                className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-warm-blue shadow-soft-sm transition-[left] duration-200 ease-out ${
                  viewType === "board" ? "left-[50%]" : "left-1"
                }`}
                style={{
                  opacity: viewType === "emails" || viewType === "captures" ? 0 : 1,
                }}
              />
              <Tooltip content="Calendar view (C)" placement="bottom">
                <button
                  onClick={() => onViewTypeChange("calendar")}
                  aria-pressed={viewType === "calendar"}
                  aria-label="Calendar view"
                  data-shortcut="C"
                  data-shortcut-group="view-toggle"
                  className={`relative z-10 flex items-center justify-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors duration-150 ${
                    viewType === "calendar"
                      ? "text-white"
                      : "text-warm-text-secondary hover:text-warm-text-primary"
                  }`}
                >
                  <CalendarDays size={15} />
                  <span className="hidden sm:inline">Calendar</span>
                </button>
              </Tooltip>
              <Tooltip content="Board view (B)" placement="bottom">
                <button
                  onClick={() => onViewTypeChange("board")}
                  aria-pressed={viewType === "board"}
                  aria-label="Board view"
                  data-shortcut="B"
                  data-shortcut-group="view-toggle"
                  className={`relative z-10 flex items-center justify-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors duration-150 ${
                    viewType === "board"
                      ? "text-white"
                      : "text-warm-text-secondary hover:text-warm-text-primary"
                  }`}
                >
                  <LayoutGrid size={15} />
                  <span className="hidden sm:inline">Board</span>
                </button>
              </Tooltip>
            </div>

            {/* Captures button — only shown when captures are waiting; icon + count
                badge, sits next to the Board/Calendar toggle. */}
            {captureCount > 0 && (
              <Tooltip content="Captures" placement="bottom">
                <button
                  onClick={() => onViewTypeChange("captures")}
                  aria-pressed={viewType === "captures"}
                  aria-label={`Captures view (${captureCount} waiting)`}
                  className={`relative flex items-center justify-center p-2 sm:p-2.5 rounded-full border transition-colors duration-150 ${
                    viewType === "captures"
                      ? "bg-warm-blue text-white border-warm-blue shadow-soft-sm"
                      : "bg-warm-bg-elevated text-warm-text-secondary border-warm-border hover:text-warm-text-primary"
                  }`}
                >
                  <Inbox size={18} />
                  <span
                    className={`absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] flex items-center justify-center rounded-full text-[10px] font-semibold leading-none tabular-nums px-1 ${
                      viewType === "captures"
                        ? "bg-white text-warm-blue"
                        : "bg-warm-blue text-white"
                    }`}
                  >
                    {captureCount > 99 ? "99+" : captureCount}
                  </span>
                </button>
              </Tooltip>
            )}

            <Tooltip
              content={`Settings (${formatShortcut("⌥S")})`}
              placement="bottom"
              align="end"
            >
              <button
                onClick={onOpenSettings}
                data-shortcut={formatShortcut("⌥S")}
                data-shortcut-badge-position="right"
                className="p-2 sm:p-2.5 text-warm-text-secondary hover:text-warm-text-primary hover:bg-warm-bg-elevated rounded-soft transition-all duration-150"
                aria-label="Settings"
              >
                <Settings size={18} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
